import React, { useEffect, useState } from "react";
import { getAttachmentPreviewUrl, DocumentUploadFeature } from "services/documentUploadService";
import { PhotoIcon } from "@heroicons/react/24/outline";

// Selection option photos are stored as a raw S3 object key (e.g.
// "selection-option-docs/<companyId>/<file>.jpeg"), not a browsable URL — every other
// attachment/document feature in this app resolves that key into a short-lived presigned URL
// via getAttachmentPreviewUrl() before rendering it as an <img>.
const previewUrlCache = new Map();

export function SelectionMediaImage({ fileKey: rawFileKey, alt = "", className = "", fallbackColor = null }) {
  const fileKey = typeof rawFileKey === "string"
    ? rawFileKey
    : rawFileKey?.fileUrl || rawFileKey?.FileUrl || null;

  const [resolvedUrl, setResolvedUrl] = useState(() => {
    if (!fileKey) return null;
    if (/^https?:\/\//i.test(fileKey) || fileKey.startsWith("blob:") || fileKey.startsWith("data:")) {
      return fileKey;
    }
    return previewUrlCache.get(fileKey) || null;
  });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!fileKey) {
      setResolvedUrl(null);
      setFailed(false);
      return undefined;
    }

    // Already directly usable (external link, blob preview, or a data URI) — skip resolution.
    if (/^https?:\/\//i.test(fileKey) || fileKey.startsWith("blob:") || fileKey.startsWith("data:")) {
      setResolvedUrl(fileKey);
      return undefined;
    }

    if (previewUrlCache.has(fileKey)) {
      setResolvedUrl(previewUrlCache.get(fileKey));
      return undefined;
    }

    setResolvedUrl(null);
    setFailed(false);

    getAttachmentPreviewUrl(fileKey, null, DocumentUploadFeature.SELECTION_OPTION_ATTACHMENT).then((res) => {
      if (cancelled) return;
      if (res.success && res.url) {
        previewUrlCache.set(fileKey, res.url);
        setResolvedUrl(res.url);
      } else {
        setFailed(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fileKey]);

  if (!fileKey || failed) {
    return (
      <div
        className={`${className} bg-slate-100 dark:bg-dark-700 flex items-center justify-center text-slate-300 dark:text-dark-500`}
        style={fallbackColor ? { backgroundColor: fallbackColor } : undefined}
      >
        <PhotoIcon className="w-8 h-8" />
      </div>
    );
  }

  if (!resolvedUrl) {
    return <div className={`${className} bg-slate-100 dark:bg-dark-700 animate-pulse`} />;
  }

  return (
    <img
      src={resolvedUrl}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}
